// src/components/hooks/useImageSelection.js
import { useEffect, useState, useCallback } from "react";
import { revokeUrlSafe } from "../backend/companies/utils/companyUtils";

const IMAGE_MAX_WIDTH = 2400;
const IMAGE_MAX_HEIGHT = 1800;
const IMAGE_QUALITY = 0.86;

function imageTargetName(file) {
    const raw = file?.name || "upload";
    const base = raw.replace(/\.[^.]+$/, "") || "upload";
    return `${base}.jpg`;
}

function loadImageFromFile(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("Bild konnte nicht gelesen werden."));
        };
        img.src = url;
    });
}

async function compressImageFile(file) {
    if (!file || !file.type?.startsWith("image/")) return file;
    if (file.type === "image/svg+xml" || file.type === "image/gif") return file;

    const img = await loadImageFromFile(file);
    const scale = Math.min(
        1,
        IMAGE_MAX_WIDTH / (img.naturalWidth || img.width || IMAGE_MAX_WIDTH),
        IMAGE_MAX_HEIGHT / (img.naturalHeight || img.height || IMAGE_MAX_HEIGHT)
    );

    const width = Math.max(1, Math.round((img.naturalWidth || img.width) * scale));
    const height = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, "image/jpeg", IMAGE_QUALITY);
    });

    if (!blob || blob.size >= file.size) return file;
    return new File([blob], imageTargetName(file), {
        type: "image/jpeg",
        lastModified: Date.now(),
    });
}

export default function useImageSelection() {
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState("");
    const [removeLogo, setRemoveLogo] = useState(false);
    useEffect(() => () => revokeUrlSafe(logoPreview), [logoPreview]);
    const pickLogo = useCallback((file) => {
        if (!file || !["image/png", "image/jpeg", "image/webp"].includes(file.type)) return;
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
        setRemoveLogo(false);
    }, []);
    const clearLogo = useCallback(() => {
        setLogoFile(null);
        setLogoPreview("");
        setRemoveLogo(true);
    }, []);
    const [startImageFile, setStartImageFile] = useState(null);
    const [startImagePreview, setStartImagePreview] = useState("");
    const [startImageCrop, setStartImageCrop] = useState({ x: 50, y: 50, zoom: 1, aspect: 2.5 });
    const [extraImageFiles, setExtraImageFiles] = useState([]); // [{file, preview}]

    useEffect(() => {
        // Revoke start image preview URL on cleanup/change
        return () => revokeUrlSafe(startImagePreview);
    }, [startImagePreview]);

    useEffect(() => {
        // Revoke extra previews on unmount
        return () => {
            extraImageFiles.forEach((x) => revokeUrlSafe(x.preview));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reset = useCallback(() => {
        setLogoFile(null);
        setLogoPreview("");
        setRemoveLogo(false);
        revokeUrlSafe(startImagePreview);
        setStartImageFile(null);
        setStartImagePreview("");
        setStartImageCrop({ x: 50, y: 50, zoom: 1, aspect: 2.5 });

        extraImageFiles.forEach((x) => revokeUrlSafe(x.preview));
        setExtraImageFiles([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startImagePreview, extraImageFiles]);

    const pickStartImage = useCallback(
        async (file) => {
            revokeUrlSafe(startImagePreview);
            if (!file) {
                setStartImageFile(null);
                setStartImagePreview("");
                setStartImageCrop({ x: 50, y: 50, zoom: 1, aspect: 2.5 });
                return;
            }
            const uploadFile = await compressImageFile(file);
            setStartImageFile(uploadFile);
            setStartImagePreview(URL.createObjectURL(uploadFile));
            setStartImageCrop({ x: 50, y: 50, zoom: 1, aspect: 2.5 });
        },
        [startImagePreview]
    );

    const addExtraImages = useCallback(async (files) => {
        const arr = Array.from(files || []);
        if (!arr.length) return;

        const mapped = await Promise.all(
            arr.map(async (f) => {
                const uploadFile = await compressImageFile(f);
                return {
                    file: uploadFile,
                    preview: URL.createObjectURL(uploadFile),
                };
            })
        );

        setExtraImageFiles((prev) => [...prev, ...mapped]);
    }, []);

    const removeExtraSelected = useCallback((idx) => {
        setExtraImageFiles((prev) => {
            const next = [...prev];
            const item = next[idx];
            if (item) revokeUrlSafe(item.preview);
            next.splice(idx, 1);
            return next;
        });
    }, []);

    // -------------------------
    // NEU: Reihenfolge ändern
    // -------------------------
    const moveExtraSelected = useCallback((fromIndex, toIndex) => {
        setExtraImageFiles((prev) => {
            const next = [...prev];

            if (
                fromIndex < 0 ||
                toIndex < 0 ||
                fromIndex >= next.length ||
                toIndex >= next.length
            ) {
                return prev;
            }

            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);

            return next;
        });
    }, []);

    return {
        logoFile, logoPreview, removeLogo, pickLogo, clearLogo,
        startImageFile,
        startImagePreview,
        startImageCrop,
        extraImageFiles,

        pickStartImage,
        setStartImageCrop,
        addExtraImages,
        removeExtraSelected,
        moveExtraSelected,

        reset,

        // optional setters (wie vorher)
        setStartImageFile,
        setStartImagePreview,
        setExtraImageFiles,
    };
}
